import { z } from "zod";
import { fileChatDestination, WORKSPACE_APPS, workspaceResourcePaths } from "./workspace.js";
import { workspaceDestination } from "./workspace-session.js";

const identity = z.string().regex(/^[a-zA-Z0-9_-]{1,200}$/);
/** A saved destination, never a session grant, arbitrary URL, local draft or window layout. */
export const WorkspaceHandoffSchema = z
  .object({
    version: z.union([z.literal(1), z.literal(2)]),
    spaceId: identity,
    app: z.enum(["home", "apps", "shared-bots", ...WORKSPACE_APPS.map((app) => app.id)]),
    id: identity.optional(),
    group: z.boolean().optional(),
    messageId: identity.optional(),
    peerBotId: identity.optional(),
    routineId: identity.optional(),
    terminal: z.literal(true).optional(),
    view: z.enum(["overview", "work", "history"]).optional(),
  })
  .strict()
  .refine((value) => value.app !== "editor" || Boolean(value.id))
  .refine((value) => {
    const variant = Boolean(value.peerBotId || value.routineId || value.terminal);
    return (
      !variant ||
      (value.version === 2 &&
        value.app === "messages" &&
        Boolean(value.id) &&
        !(value.group && (value.peerBotId || value.routineId)) &&
        !(value.peerBotId && value.routineId))
    );
  });
export type WorkspaceHandoff = z.infer<typeof WorkspaceHandoffSchema>;

export function captureWorkspaceHandoff(
  path: string,
  platform: "web" | "mobile",
  spaceId: string | null,
): WorkspaceHandoff | null {
  const destination = workspaceDestination(path, platform);
  if (!destination || !spaceId) return null;
  const url = new URL(destination.path, "https://workspace.invalid");
  const query = url.searchParams;
  let id: string | undefined;
  let group: boolean | undefined;
  if (destination.id === "files") {
    id =
      platform === "web"
        ? url.pathname.match(/^\/app\/artifacts\/([^/]+)$/)?.[1]
        : (query.get("artifactId") ?? undefined);
    if (id) id = query.get("version") ?? id;
  } else if (destination.id === "editor") id = query.get("artifact") ?? undefined;
  else if (destination.id === "missions") id = query.get("id") ?? undefined;
  else if (destination.id === "projects")
    id = query.get("ticket") ?? query.get("ticketId") ?? undefined;
  else if (destination.id === "computers") id = query.get("computer") ?? undefined;
  else if (destination.id === "messages") {
    group =
      platform === "web" ? url.pathname.startsWith("/app/g/") : url.pathname === "/group-thread";
    id =
      platform === "web"
        ? url.pathname === "/app/messages"
          ? undefined
          : url.pathname.split("/").at(-1)
        : (query.get(group ? "groupId" : "botId") ?? undefined);
  }
  const variants =
    destination.id === "messages"
      ? {
          ...(query.get("peerBotId") ? { peerBotId: query.get("peerBotId") } : {}),
          ...(query.get("routine") || query.get("routineId")
            ? { routineId: query.get("routine") || query.get("routineId") }
            : {}),
          ...(query.get("terminal") === "1" ? { terminal: true } : {}),
        }
      : {};
  const parsed = WorkspaceHandoffSchema.safeParse({
    version: Object.keys(variants).length ? 2 : 1,
    ...variants,
    spaceId,
    app: destination.id,
    ...(id ? { id } : {}),
    ...(group ? { group } : {}),
    ...(destination.id === "messages" && (query.get("m") || query.get("messageId"))
      ? { messageId: query.get("m") || query.get("messageId") }
      : {}),
    ...(destination.id === "missions" && query.get("view") ? { view: query.get("view") } : {}),
  });
  return parsed.success ? parsed.data : null;
}

export function workspaceHandoffDestination(value: WorkspaceHandoff) {
  const ref = WorkspaceHandoffSchema.parse(value);
  const definition = WORKSPACE_APPS.find((app) => app.id === ref.app);
  let paths = definition
    ? { webPath: definition.web as string, mobilePath: definition.mobile as string }
    : {
        webPath:
          ref.app === "home"
            ? "/app"
            : ref.app === "apps"
              ? "/app/workspace/apps"
              : "/app/shared-bots",
        mobilePath:
          ref.app === "home" ? "/" : ref.app === "apps" ? "/workspace/apps" : "/shared-bots",
      };
  if (ref.id) {
    const hit = { id: ref.id, title: "", subtitle: "" };
    if (ref.app === "files")
      paths = workspaceResourcePaths({ ...hit, kind: "file", artifactId: ref.id })!;
    if (ref.app === "missions")
      paths = workspaceResourcePaths({ ...hit, kind: "mission", missionId: ref.id })!;
    if (ref.app === "projects")
      paths = workspaceResourcePaths({ ...hit, kind: "ticket", ticketId: ref.id })!;
    if (ref.app === "computers") paths = workspaceResourcePaths({ ...hit, kind: "computer" })!;
    if (ref.app === "messages")
      paths = workspaceResourcePaths({
        ...hit,
        kind: "message",
        ...(ref.group ? { groupId: ref.id } : { botId: ref.id }),
        messageId: ref.messageId,
      })!;
    if (ref.app === "editor")
      paths = {
        webPath: `/app/editor?artifact=${ref.id}`,
        mobilePath: `/editor?artifact=${ref.id}`,
      };
  }
  if (ref.app === "messages" && ref.id) {
    const web = new URL(paths.webPath, "https://workspace.invalid");
    const mobile = new URL(paths.mobilePath, "https://workspace.invalid");
    if (ref.peerBotId) {
      web.searchParams.set("peerBotId", ref.peerBotId);
      mobile.pathname = "/peer-messages";
      mobile.searchParams.set("peerBotId", ref.peerBotId);
    }
    if (ref.routineId) {
      web.searchParams.set("routine", ref.routineId);
      mobile.pathname = "/routine";
      mobile.searchParams.set("routineId", ref.routineId);
    }
    if (ref.terminal) {
      web.searchParams.set("terminal", "1");
      mobile.searchParams.set("terminal", "1");
    }
    paths = { webPath: web.pathname + web.search, mobilePath: mobile.pathname + mobile.search };
  }
  if (ref.app === "missions" && ref.id && ref.view) {
    paths.webPath += `&view=${ref.view}`;
    paths.mobilePath += `&view=${ref.view}`;
  }
  return {
    ...paths,
    name:
      definition?.name ??
      (ref.app === "home" ? "Home" : ref.app === "apps" ? "Apps" : "Shared bots"),
  };
}

/** Reuse the composer handoff for the exact saved document currently in focus. */
export function workspaceFileChatDestination(
  path: string,
  platform: "web" | "mobile",
  spaceId: string | null,
  recipient: { id: string; spaceId: string },
) {
  const ref = captureWorkspaceHandoff(path, platform, spaceId);
  return ref?.id && (ref.app === "files" || ref.app === "editor")
    ? fileChatDestination(ref.id, ref.spaceId, recipient)
    : null;
}

export function workspaceHandoffLink(origin: string, value: WorkspaceHandoff) {
  const url = new URL("/continue", origin);
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password)
    throw new Error("Invalid server address");
  url.searchParams.set("handoff", JSON.stringify(WorkspaceHandoffSchema.parse(value)));
  return url.href;
}
export function parseWorkspaceHandoff(value: string | null | undefined): WorkspaceHandoff | null {
  if (!value || value.length > 2048) return null;
  try {
    const parsed = WorkspaceHandoffSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
